import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import { forexApi, type ConfigPackage, type ConfigRelease } from "@/lib/forex-api";

function formatTime(timestamp?: number | null) {
  if (!timestamp) return "未发布";
  return new Date(timestamp * 1000).toLocaleString("sv-SE").slice(0, 19);
}

export default function ConfigDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const configKey = searchParams.get("pkg") || searchParams.get("type") || "app_terms";
  const [pkg, setPkg] = useState<ConfigPackage | null>(null);
  const [releases, setReleases] = useState<ConfigRelease[]>([]);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [rolling, setRolling] = useState("");
  const [error, setError] = useState("");
  const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  const loadConfig = useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([forexApi.getConfigPackage(configKey), forexApi.listConfigReleases()])
      .then(([packageResult, releaseResult]) => {
        setPkg(packageResult);
        setDraft(JSON.stringify(packageResult.payload, null, 2));
        setReleases(releaseResult.items.filter((item) => item.config_key === packageResult.config_key));
      })
      .catch(() => setError("配置详情加载失败，请检查后端服务或登录态。"))
      .finally(() => setLoading(false));
  }, [configKey]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const showToast = (type: "success" | "error", msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  };

  const parseDraft = () => {
    try {
      return JSON.parse(draft) as Record<string, unknown>;
    } catch {
      showToast("error", "JSON 格式错误，请修正后再保存。");
      return null;
    }
  };

  const handleSaveDraft = async () => {
    if (!pkg) return;
    const payload = parseDraft();
    if (!payload) return;
    setSaving(true);
    try {
      const updated = await forexApi.patchConfigPackage(pkg.config_key, { payload });
      setPkg(updated);
      setDraft(JSON.stringify(updated.payload, null, 2));
      setEditing(false);
      showToast("success", "草稿已保存");
    } catch {
      showToast("error", "草稿保存失败，请确认后端服务和登录态。");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!pkg) return;
    setPublishing(true);
    try {
      const result = await forexApi.publishConfigPackage(pkg.config_key, { description: `${pkg.label} 生成后台发布记录` });
      setPkg(result.package);
      setReleases((prev) => [result.release, ...prev]);
      showToast("success", "后台发布记录已生成；C 端实际读取结果待核对。");
    } catch {
      showToast("error", "配置发布失败，请确认后端服务和登录态。");
    } finally {
      setPublishing(false);
    }
  };

  const handleRollback = async (releaseId: string) => {
    if (!pkg) return;
    setRolling(releaseId);
    try {
      const result = await forexApi.rollbackConfigPackage(pkg.config_key, releaseId);
      setPkg(result.package);
      setReleases((prev) => prev.map((item) => (item.release_id === result.release.release_id ? result.release : item)));
      showToast("success", "配置已回滚");
    } catch {
      showToast("error", "配置回滚失败，请确认后端服务和登录态。");
    } finally {
      setRolling("");
    }
  };

  return (
    <div className="space-y-5">
      {configKey === "risk_config" && <p role="status" className="text-sm text-amber-700">此处为后台配置记录；逐账号执行风控以已审核策略、授权与实际准入证据为准。</p>}
      <Breadcrumb items={[{ label: "管理后台", href: "/forex/overview" }, { label: "风控与配置", href: "/forex/config" }, { label: "配置详情" }]} />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">{pkg?.label || configKey}</h2>
          <p className="text-xs text-text-muted mt-0.5">{pkg?.description || "配置包详情"} · {configKey}</p>
          <p className="text-[10px] mt-1 text-emerald-500">目标接口 GET /app/config/bootstrap {"->"} {configKey}（实际生效待核对）</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand cursor-pointer whitespace-nowrap"><i className="ri-arrow-left-line text-sm" />返回配置中心</button>
        </div>
      </div>

      {loading && <div className="bg-white rounded-lg border border-surface-border px-4 py-10 text-center text-sm text-text-muted">加载中...</div>}
      {error && <div className="rounded-lg border border-red-500/20 bg-red-500/5 px-4 py-3 text-sm text-red-500">{error}</div>}

      {!loading && pkg && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[10px] text-text-muted">状态</p><p className={`text-sm font-semibold mt-1 ${pkg.status === "enabled" ? "text-emerald-500" : "text-red-500"}`}>{pkg.status === "enabled" ? "已启用" : "已停用"}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[10px] text-text-muted">版本</p><p className="text-sm font-semibold font-mono mt-1 text-text-primary">{pkg.version}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[10px] text-text-muted">最后发布</p><p className="text-sm font-semibold font-mono mt-1 text-text-primary">{formatTime(pkg.last_published_at)}</p></div>
            <div className="bg-white rounded-lg border border-surface-border px-4 py-3"><p className="text-[10px] text-text-muted">影响 API</p><p className="text-sm font-semibold font-mono mt-1 text-brand">{pkg.api_path}</p></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-surface-border p-4 lg:col-span-2">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-text-primary">配置 JSON</h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => setEditing((prev) => !prev)} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface text-text-secondary hover:bg-brand/10 hover:text-brand">{editing ? "取消编辑" : "编辑"}</button>
                  <button onClick={handleSaveDraft} disabled={!editing || saving} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-brand text-white disabled:opacity-50">{saving ? "保存中" : "保存草稿"}</button>
                  <button onClick={handlePublish} disabled={publishing} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500 text-white disabled:opacity-50">{publishing ? "发布中" : "生成发布记录"}</button>
                </div>
              </div>
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} readOnly={!editing} className={`w-full min-h-[420px] rounded-lg border border-surface-border px-3 py-2 text-xs font-mono focus:border-brand/50 focus:outline-none ${editing ? "bg-white text-text-primary" : "bg-surface/50 text-text-secondary"}`} />
            </div>

            <div className="bg-white rounded-lg border border-surface-border p-4">
              <h3 className="text-sm font-semibold text-text-primary mb-3">发布记录</h3>
              <div className="space-y-2">
                {releases.map((release) => (
                  <div key={release.release_id} className="rounded-lg border border-surface-border p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold text-text-primary font-mono">{release.version}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${release.status === "active" ? "bg-emerald-500/10 text-emerald-500" : release.status === "rolled_back" ? "bg-red-500/10 text-red-500" : "bg-surface text-text-muted"}`}>{release.status}</span>
                    </div>
                    <p className="text-[10px] text-text-muted mt-1">{formatTime(release.published_at)}</p>
                    <p className="text-[11px] text-text-secondary mt-1">{release.description}</p>
                    {release.status !== "rolled_back" && (
                      <button onClick={() => handleRollback(release.release_id)} disabled={rolling === release.release_id} className="mt-2 px-2 py-1 rounded-md text-[11px] font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 disabled:opacity-60">{rolling === release.release_id ? "回滚中" : "回滚"}</button>
                    )}
                  </div>
                ))}
                {releases.length === 0 && <div className="text-sm text-text-muted text-center py-8">暂无发布记录</div>}
              </div>
            </div>
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
            <i className={`${toast.type === "success" ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"} text-base`} />{toast.msg}
          </div>
        </div>
      )}
    </div>
  );
}
